/**
 * Shared PDP recommendations source (window.RTLRecs).
 *
 * All three PDP surfaces — the buy-box sidebar grid (`product-info`), the
 * full-width "You may also like" rail (`product-recommendations`), and the
 * "Customers also viewed" tab (`recently-viewed`) — pull from ONE Algolia
 * Recommend call made here, not three of their own.
 *
 * Why a shared list rather than per-section fetches
 * -------------------------------------------------
 * The Shopify-native implementation deduped the sidebar against the rail
 * POSITIONALLY: the sidebar rendered the first 3 related products and
 * `recently-viewed` skipped them with `offset: 3`. That contract was already
 * broken — the sidebar applies a gender gate BEFORE taking its 3, while the
 * `offset: 3` was applied to the raw API result, so on a PDP whose top 3
 * related products include an opposite-gender item the two surfaces overlap.
 *
 * Here every surface filters the same ranked, already-gated list and then
 * claims a slice from it, so "the sidebar's 3" and "the rail's next N" are
 * the same 3 and next N by construction. Ordering is Algolia's ranking.
 *
 * Contract with the headless storefront (tools/apis/algolia/index.ts):
 *   - model `related-products`, threshold 0
 *   - objectID is the product's FIRST VARIANT id (Algolia's Shopify
 *     integration keys records by variant; a product id returns nothing)
 *   - on any error / empty result the caller falls back to Shopify's native
 *     `related` recommendations
 *
 * Public API (window.RTLRecs):
 *   - handles({ objectID, model, gender, exclude, limit }) → Promise<string[]>
 *       Ranked, deduped, gender-gated, exclusion-filtered product handles.
 *       Memoised per objectID+model, so N surfaces cost 1 network call.
 *   - claim(key, handles, count) → string[]
 *       Take the next `count` unclaimed handles. Surfaces call this in DOM
 *       order so no product appears on two surfaces of the same page.
 *   - release(key, handles) → void
 *       Return claimed handles to the pool when a surface renders none of
 *       them, so a later surface can still use them.
 *   - fetchCard(handle, sectionId) → Promise<Element|null>
 *       Resolve a handle into a rendered `.product-card` via the Section
 *       Rendering API. Null when the card is suppressed (#204 OOS rule).
 *       Tagged with its Recommend hit for Insights (below) when tracked.
 *   - cards(handles, sectionId) → Promise<Element[]>
 *       fetchCard over a list, preserving rank, dropping suppressed cards.
 *   - ready(timeoutMs) → Promise<RTLAlgolia>
 *       Resolve once `algolia-client.js` has defined window.RTLAlgolia.
 *
 * Insights (#341)
 * ---------------
 * The Recommend dashboard needs click and conversion events carrying the
 * `queryID` of the Recommend response. `getRecommendations()` asks for one
 * when analytics consent allows it, so the one shared call waits on
 * `RTLAlgoliaInsights.whenReady()` first. Cards come back from the Section
 * Rendering API knowing nothing of Algolia, so `fetchCard` tags each one with
 * its hit (`data-insights-*`, the same attributes the search cards carry) and
 * the delegated listeners at the bottom send the events from tagged cards
 * only. Shopify-fallback cards never pass through `fetchCard`, so they stay
 * untagged and send nothing. `data-insights-position` is the rank the shopper
 * saw, so `cards()` stamps it per surface once the suppressed cards are out.
 */
(function () {
  'use strict';

  var DEFAULT_MODEL = 'related-products';
  var CARD_SECTION_ID = 'algolia-recommendation-card';

  // objectID + model -> Promise<hit[]>. One in-flight request per key.
  var hitCache = {};
  // Handles already rendered by an earlier surface on this page.
  var claimed = {};
  // Handle -> { index, queryID, objectID, productId, position } from the
  // latest Recommend response that ranked it (#341).
  const insightsByHandle = {};
  const TAGGED_CARD = '[data-insights-source="recommend"]';

  /**
   * `algolia-client.js` is loaded with `<script defer>`, but section javascript
   * blocks are bundled into section assets that can execute AHEAD of the
   * head-defer queue. Reading window.RTLAlgolia synchronously therefore loses a
   * race and drops straight to the Shopify fallback, so Algolia never gets
   * called at all.
   *
   * Deliberately NOT `RTLAlgolia.ready()` — that also waits on the
   * algoliasearch AND instantsearch UMD bundles, neither of which these rails
   * need (Recommend goes over raw fetch; instantsearch only loads on /search).
   */
  function ready(timeoutMs) {
    var deadline = Date.now() + (timeoutMs || 5000);
    return new Promise(function (resolve, reject) {
      (function tick() {
        var a = window.RTLAlgolia;
        if (a && typeof a.getRecommendations === 'function') {
          resolve(a);
          return;
        }
        if (Date.now() > deadline) {
          reject(new Error('RTLAlgolia unavailable'));
          return;
        }
        window.requestAnimationFrame(tick);
      })();
    });
  }

  // Resolves once `RTLAlgoliaInsights` has its consent answer (never rejects),
  // or straight away when Insights is not on the page.
  function insightsReady() {
    const insights = window.RTLAlgoliaInsights;
    return insights && typeof insights.whenReady === 'function'
      ? insights.whenReady()
      : Promise.resolve();
  }

  /**
   * Index a Recommend response by product handle so `fetchCard` can tag each
   * card with the hit it came from. The first (highest-ranked) variant record
   * of a product is the one `hitsToHandles` keeps, so it is the one tagged.
   * An untracked response (no consent) clears its handles, so a card is never
   * tagged with an older response's `queryID`.
   */
  function rememberInsights(hits) {
    const seen = {};
    (hits || []).forEach(function (hit) {
      const handle = hit ? window.RTLAlgolia.hitsToHandles([hit])[0] : '';
      if (!handle || seen[handle]) return;
      seen[handle] = true;
      if (!hit.__queryID) {
        delete insightsByHandle[handle];
        return;
      }
      insightsByHandle[handle] = {
        index: hit.__indexName,
        queryID: hit.__queryID,
        objectID: hit.objectID,
        productId: hit.id,
        position: hit.__position,
      };
    });
  }

  function tagCard(card, handle) {
    const insights = insightsByHandle[handle];
    if (!insights) return;
    card.setAttribute('data-insights-source', 'recommend');
    card.setAttribute('data-insights-index', insights.index);
    card.setAttribute('data-insights-query-id', insights.queryID);
    card.setAttribute('data-insights-object-id', insights.objectID);
    card.setAttribute('data-insights-product-id', insights.productId || '');
    // Rank in the shared Recommend response. `cards()` re-stamps this with
    // the position the shopper actually saw; a lone `fetchCard` keeps it.
    card.setAttribute('data-insights-position', insights.position);
  }

  function readTag(card) {
    return {
      index: card.getAttribute('data-insights-index'),
      queryID: card.getAttribute('data-insights-query-id'),
      objectID: card.getAttribute('data-insights-object-id'),
      productId: card.getAttribute('data-insights-product-id'),
      position: card.getAttribute('data-insights-position'),
    };
  }

  /**
   * Algolia hit tags arrive as an array or a comma-joined string depending on
   * how the Shopify integration serialised the record. An unreadable tag list
   * passes the gate — the resolved card is still filtered by the exclusion
   * list, and silently dropping every hit because the index shape differs is
   * the worse failure.
   */
  function matchesGender(hit, gender) {
    if (!gender) return true;
    var tags = hit && hit.tags;
    if (typeof tags === 'string') tags = tags.split(',');
    if (!Array.isArray(tags)) return true;
    return tags.some(function (tag) {
      return String(tag).trim().toLowerCase() === gender;
    });
  }

  function handles(options) {
    var opts = options || {};
    var objectID = opts.objectID;
    var model = opts.model || DEFAULT_MODEL;
    var gender = (opts.gender || '').toLowerCase();
    var exclude = opts.exclude || '';
    // Headroom matters: the three surfaces claim 3 + up to 10 (the rail's max
    // `limit` setting) + 8 = 21, so a 20-cap silently starved the tab at the
    // top of that range. Match `maxRecommendations` and let the surfaces cap.
    var limit = parseInt(opts.limit, 10) || 30;

    if (!objectID) return Promise.reject(new Error('objectID required'));

    var key = objectID + '::' + model;
    if (!hitCache[key]) {
      hitCache[key] = ready()
        .then(function (algolia) {
          // Recommend only asks for a `queryID` once analytics consent allows
          // it, so give Shopify's consent API the same short wait `/search`
          // gives it before its first query.
          return insightsReady().then(function () {
            return algolia.getRecommendations({
              objectID: objectID,
              model: model,
              // Over-request so the gender / sibling / OOS filters still leave
              // enough to fill every surface on the page.
              maxRecommendations: 30,
            });
          });
        })
        .then(function (hits) {
          rememberInsights(hits);
          return hits;
        });
    }

    return hitCache[key].then(function (hits) {
      return window.RTLAlgolia.hitsToHandles(
        (hits || []).filter(function (hit) {
          return matchesGender(hit, gender);
        })
      )
        .filter(function (handle) {
          return exclude.indexOf(',' + handle + ',') === -1;
        })
        .slice(0, limit);
    });
  }

  /**
   * Take the next `count` handles not already claimed on this page, so the
   * sidebar, the rail and the tab never show the same product twice. `key`
   * scopes the claim set per source product so a soft navigation to another
   * PDP starts clean.
   */
  function claim(key, list, count) {
    if (!claimed[key]) claimed[key] = {};
    var taken = [];
    for (var i = 0; i < list.length && taken.length < count; i += 1) {
      if (claimed[key][list[i]]) continue;
      claimed[key][list[i]] = true;
      taken.push(list[i]);
    }
    return taken;
  }

  /**
   * Hand claimed handles back to the pool. A surface that claims a slice and
   * then renders nothing from it (every card suppressed by the #204 OOS rule)
   * would otherwise burn those handles: they stay claimed, the later surfaces
   * can never use them, and the failing surface drops to Shopify `related`
   * whose results may duplicate what the other surfaces already show.
   */
  function release(key, list) {
    if (!claimed[key]) return;
    (list || []).forEach(function (handle) {
      delete claimed[key][handle];
    });
  }

  function fetchCard(handle, sectionId) {
    var url =
      '/products/' +
      encodeURIComponent(handle) +
      '?section_id=' +
      encodeURIComponent(sectionId || CARD_SECTION_ID);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('card fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        // Missing .product-card means the shared OOS rule (#204) suppressed it.
        const card = doc.querySelector('.product-card');
        if (card) tagCard(card, handle);
        return card;
      })
      .catch(function () {
        return null;
      });
  }

  function cards(list, sectionId) {
    // Fetch in parallel, then re-order to Algolia's ranking — the requests
    // settle out of order and rank is the whole point.
    return Promise.all(
      (list || []).map(function (handle) {
        return fetchCard(handle, sectionId);
      })
    ).then(function (nodes) {
      const rendered = nodes.filter(Boolean);
      // Insights wants the position the shopper saw, and the rank `tagCard`
      // stamped is the one from the shared Recommend response: every surface
      // claims a different slice of it, and the #204 OOS rule drops cards out
      // of the middle, so the rail's first card carries 4 and the tab's 14.
      // Re-stamp per surface now the surviving order is known (#341).
      rendered.forEach(function (card, i) {
        if (card.matches(TAGGED_CARD)) {
          card.setAttribute('data-insights-position', i + 1);
        }
      });
      return rendered;
    });
  }

  /**
   * Insights events from tagged cards (#341). Delegated, because the cards
   * are fetched in after load, and in the capture phase so they run ahead of
   * the card's own handlers -- quick-add posts the add on `change`.
   *
   *   - Product link (image or title): click event, and the recommendation is
   *     remembered for an add to cart on the PDP it opens. Colour swatches
   *     link to a SIBLING product, so they are not a click on this
   *     recommendation and an add there could never be matched back to it.
   *   - Quick-add size: remembered only, so the add that follows is
   *     attributed. No click event -- the shopper never opened the product.
   *
   * `RTLAlgoliaInsights` re-checks consent before sending or storing anything.
   */
  document.addEventListener(
    'click',
    function (event) {
      const insights = window.RTLAlgoliaInsights;
      const target = event.target;
      if (!insights || !(target instanceof Element)) return;
      const link = target.closest('a[href]');
      if (!link || link.classList.contains('product-card__swatch')) return;
      const card = link.closest(TAGGED_CARD);
      if (card) insights.clickedRecommendation(readTag(card));
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      const insights = window.RTLAlgoliaInsights;
      const target = event.target;
      if (!insights || !(target instanceof Element)) return;
      if (!target.matches('[data-action="quick-add"]')) return;
      const card = target.closest(TAGGED_CARD);
      if (card) insights.attributeRecommendation(readTag(card));
    },
    true
  );

  window.RTLRecs = {
    ready: ready,
    handles: handles,
    claim: claim,
    release: release,
    fetchCard: fetchCard,
    cards: cards,
    CARD_SECTION_ID: CARD_SECTION_ID,
  };

  // Release any surface that queued against `RTLRecsReady` (defined inline in
  // theme.liquid) before this deferred file ran. Sections must await that
  // promise rather than read `window.RTLRecs` synchronously.
  if (typeof window.__rtlRecsResolve === 'function') {
    window.__rtlRecsResolve(window.RTLRecs);
  } else {
    // Asset loaded without the inline bootstrap (e.g. a non-product template
    // that includes it directly) -- keep the contract satisfiable anyway.
    window.RTLRecsReady = Promise.resolve(window.RTLRecs);
  }
})();
